import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import SectionCard from "@/components/customers/SectionCard";
import { AgreementsTable } from "./AgreementsTable";
import { AgreementFormDialog } from "./AgreementFormDialog";
import type { RecurringAgreementWithCustomer } from "@/hooks/use-recurring-agreements";
import { formatNOK } from "@/lib/format";
import { osloDateString } from "@/lib/oslo-time";
import { computeYearlyRunningRevenue, isExpiringSoon } from "@/lib/recurring-agreements-calc";
import { daysUntil } from "@/lib/customer-kpi-calc";
import { TONE_TEXT } from "@/lib/widget-status";

/**
 * VIB-1290: «Løpende avtaler» på kundekortet: teller med aktive og kr/år,
 * Autosend-kolonne, «Forny» og varsel på avtaler som utløper snart. Gjenbruker
 * AgreementsTable med kunde-kolonnen skjult. Uten avtaler er kortet én linje.
 */
export function CustomerAgreementsSection({
  customerId,
  agreements,
}: {
  customerId: string;
  agreements: RecurringAgreementWithCustomer[];
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const today = osloDateString(new Date());
  const active = agreements.filter((a) => a.status === "active");
  const expiring = active.filter((a) => isExpiringSoon(a, today));
  const yearly = computeYearlyRunningRevenue(agreements);

  return (
    <>
      <SectionCard
        title="Løpende avtaler"
        meta={active.length > 0 ? `${active.length} aktive · ${formatNOK(yearly)}/år` : undefined}
        empty="Ingen avtaler. «Ny avtale» lager en løpende avtale, utleie eller lagring."
        actions={
          <>
            <Link to="/avtaler" className="text-sm text-primary hover:underline">
              Alle avtaler
            </Link>
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
              <SrIcon name="plus" className="mr-1 h-3 w-3" /> Ny avtale
            </Button>
          </>
        }
      >
        {agreements.length === 0 ? null : (
          <div className="px-5 py-4">
            <AgreementsTable agreements={agreements} hideCustomerColumn showAutoSend />
            {expiring.map((a) => {
              const days = daysUntil(a.end_date!, new Date());
              const weeks = Math.round(days / 7);
              return (
                <p key={a.id} className={`mt-3 text-sm ${TONE_TEXT.near}`}>
                  {a.description || a.rental_object || "Avtalen"} utløper {weeks >= 2 ? `om ${weeks} uker` : `om ${days} ${days === 1 ? "dag" : "dager"}`}. Bruk «Forny» i menyen for å forlenge den.
                </p>
              );
            })}
          </div>
        )}
      </SectionCard>
      <AgreementFormDialog open={createOpen} onOpenChange={setCreateOpen} defaultCustomerId={customerId} />
    </>
  );
}
